import React, { useState } from "react";
import { Button } from "../../../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../../../foundation/altzero_base/base/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../foundation/altzero_base/base/components/ui/dropdown-menu";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  MoreHorizontal,
  Edit3,
  Trash2,
  Plus,
  FileText,
  Check,
  X,
} from "lucide-react";
import { TemplateSection, SectionMetadata } from "../../../utils/sectionUtils";
import SectionMetadataDropdown from "../SectionMetadataDropdown";

// Types
interface Section {
  id: string;
  title: string;
  content: string;
  edited?: boolean;
  order?: number;
  metadata?: SectionMetadata;
}

interface SortableSectionListProps {
  sections: Section[];
  selectedSectionId: string | null;
  selectedSectionForPreview?: string | null;
  activeTab?: 'edit' | 'section-preview';
  onSectionSelect: (sectionId: string) => void;
  onSectionReorder: (sections: Section[]) => void;
  onSectionTitleChange: (sectionId: string, newTitle: string) => void;
  onSectionDelete: (sectionId: string) => void;
  onSectionAdd: () => void;
  onSectionAddAfter: (afterSectionId: string) => void;
  onSectionEdit: (sectionId: string) => void;
  onMetadataChange?: (sectionId: string, metadata: SectionMetadata) => void;
  readonly?: boolean;
  className?: string;
}

// Individual Sortable Section Item
interface SortableSectionItemProps {
  section: Section;
  isSelected: boolean;
  isSelectedForPreview: boolean;
  activeTab?: 'edit' | 'section-preview';
  isEditingTitle: boolean;
  titleValue: string;
  onSelect: () => void;
  onEdit: () => void;
  onTitleEdit: () => void;
  onTitleChange: (value: string) => void;
  onTitleSave: () => void;
  onTitleCancel: () => void;
  onDelete: () => void;
  onAddAfter: () => void;
  onMetadataChange?: (sectionId: string, metadata: SectionMetadata) => void;
  readonly?: boolean;
}

const SortableSectionItem: React.FC<SortableSectionItemProps> = ({
  section,
  isSelected,
  isSelectedForPreview,
  activeTab,
  isEditingTitle,
  titleValue,
  onSelect,
  onEdit,
  onTitleEdit,
  onTitleChange,
  onTitleSave,
  onTitleCancel,
  onDelete,
  onAddAfter,
  onMetadataChange,
  readonly = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: readonly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 🚨 ENHANCED: Context-aware styling based on selection type
  const getSelectionStyling = () => {
    if (isSelected && activeTab === 'edit') {
      return "bg-blue-50 border-blue-300 shadow-sm"; // Edit selection (blue)
    } else if (isSelectedForPreview && activeTab === 'section-preview') {
      return "bg-green-50 border-green-300 shadow-sm"; // Preview selection (green)
    } else {
      return "bg-white hover:border-blue-200 hover:shadow-sm"; // Default
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 mb-2 border rounded-lg transition-all cursor-pointer ${getSelectionStyling()} ${isDragging ? "shadow-lg" : ""}`}
      onClick={onSelect}
    >
      <div className="flex items-start gap-2">
        {/* Drag Handle */}
        {!readonly && (
          <div
            {...attributes}
            {...listeners}
            className="flex-shrink-0 mt-1 p-1 hover:bg-gray-100 rounded cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <GripVertical className="h-4 w-4 text-gray-400" />
          </div>
        )}

        {/* Section Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-center gap-2 mb-1">
            {isEditingTitle ? (
              <div className="flex items-center gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={titleValue}
                  onChange={(e) => onTitleChange(e.target.value)}
                  className="h-7 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onTitleSave();
                    if (e.key === "Escape") onTitleCancel();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={(e) => { e.stopPropagation(); onTitleSave(); }}
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  onClick={(e) => { e.stopPropagation(); onTitleCancel(); }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <>
                <h4
                  className="font-medium text-sm cursor-pointer hover:text-blue-600 flex-1 truncate"
                  title={section.title}
                >
                  {section.title}
                </h4>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {section.edited && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      ✓
                    </span>
                  )}
                  {/* 🚨 NEW: Visual indicator for selection type */}
                  {isSelected && activeTab === 'edit' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" title="Selected for editing">
                      Edit
                    </span>
                  )}
                  {isSelectedForPreview && activeTab === 'section-preview' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" title="Selected for preview">
                      Preview
                    </span>
                  )}
                  {!readonly && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-60 hover:opacity-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                          <Edit3 className="h-3 w-3 mr-2" />
                          Edit Content
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onTitleEdit(); }}>
                          <Edit3 className="h-3 w-3 mr-2" />
                          Edit Title
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAddAfter(); }}>
                          <Plus className="h-3 w-3 mr-2" />
                          Add Section After
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-red-600">
                          <Trash2 className="h-3 w-3 mr-2" />
                          Delete Section
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Content Preview */}
          <p className="text-xs text-gray-500 line-clamp-2 mb-1">
            {section.content
              ? section.content.substring(0, 100) + (section.content.length > 100 ? "..." : "")
              : "No content"}
          </p>

          {/* Stats */}
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{section.content?.length || 0} characters</span>
            {!readonly && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-xs"
                onClick={onEdit}
              >
                Edit
              </Button>
            )}
          </div>

          {/* Metadata Dropdown - Moved to bottom */}
          {onMetadataChange && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <SectionMetadataDropdown
                sectionId={section.id}
                currentMetadata={section.metadata || { type: 'static' }}
                onMetadataChange={onMetadataChange}
                readonly={readonly}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main SortableSectionList Component
export const SortableSectionList: React.FC<SortableSectionListProps> = ({
  sections,
  selectedSectionId,
  selectedSectionForPreview,
  activeTab,
  onSectionSelect,
  onSectionReorder,
  onSectionTitleChange,
  onSectionDelete,
  onSectionAdd,
  onSectionAddAfter,
  onSectionEdit,
  onMetadataChange,
  readonly = false,
  className = "",
}) => {
  const [editingTitles, setEditingTitles] = useState<Set<string>>(new Set());
  const [titleInputs, setTitleInputs] = useState<Record<string, string>>({});

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((section) => section.id === active.id);
      const newIndex = sections.findIndex((section) => section.id === over.id);
      const reorderedSections = arrayMove(sections, oldIndex, newIndex);
      onSectionReorder(reorderedSections);
    }
  };

  // Title editing functions
  const startTitleEditing = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      setEditingTitles(prev => new Set([...prev, sectionId]));
      setTitleInputs(prev => ({ ...prev, [sectionId]: section.title }));
    }
  };

  const saveTitleEdit = (sectionId: string) => {
    const newTitle = titleInputs[sectionId];
    if (newTitle && newTitle.trim()) {
      onSectionTitleChange(sectionId, newTitle.trim());
    }
    setEditingTitles(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionId);
      return newSet;
    });
    setTitleInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[sectionId];
      return newInputs;
    });
  };

  const cancelTitleEdit = (sectionId: string) => {
    setEditingTitles(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionId);
      return newSet;
    });
    setTitleInputs(prev => {
      const newInputs = { ...prev };
      delete newInputs[sectionId];
      return newInputs;
    });
  };

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Document Sections</h3>
        <p className="text-sm text-gray-600">
          {sections.length} sections • {sections.filter(s => s.edited).length} edited
        </p>
      </div>

      {/* Section List - EXACT COPY FROM DocumentsRegeneration.tsx */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 300px)', minHeight: '200px' }}>
        <div className="p-2">
          {sections.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sections.map((section) => section.id)}
                strategy={verticalListSortingStrategy}
              >
                {sections.map((section) => (
                  <SortableSectionItem
                    key={section.id}
                    section={section}
                    isSelected={selectedSectionId === section.id}
                    isSelectedForPreview={selectedSectionForPreview === section.id}
                    activeTab={activeTab}
                    isEditingTitle={editingTitles.has(section.id)}
                    titleValue={titleInputs[section.id] || ""}
                    onSelect={() => onSectionSelect(section.id)}
                    onEdit={() => onSectionEdit(section.id)}
                    onTitleEdit={() => startTitleEditing(section.id)}
                    onTitleChange={(value) => setTitleInputs(prev => ({ ...prev, [section.id]: value }))}
                    onTitleSave={() => saveTitleEdit(section.id)}
                    onTitleCancel={() => cancelTitleEdit(section.id)}
                    onDelete={() => onSectionDelete(section.id)}
                    onAddAfter={() => onSectionAddAfter(section.id)}
                    onMetadataChange={onMetadataChange}
                    readonly={readonly}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No sections available</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Section Button */}
      {!readonly && (
        <div className="flex-shrink-0 p-2 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={onSectionAdd}
          >
            <Plus className="h-4 w-4" />
            Add Section
          </Button>
        </div>
      )}
    </div>
  );
};

export default SortableSectionList;
